import { Component, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ContactsService } from 'src/app/business-service/contacts/contacts.service';
import { GlobalService } from '../../../shared/services/global/global.service';
import { ComponentReloadService } from '../../../shared/services/reload/component-reload.service';
import { ResponseNotificationService } from '../../../shared/services/response-notification/response-notification.service';
import { EditableGridColumn } from 'src/app/modules/shared/editable-grid/editable-grid.component';

@Component({
  selector: 'app-scan-contact-modal',
  templateUrl: './scan-contact-modal.component.html',
  styleUrls: ['./scan-contact-modal.component.scss'],
})
export class ScanContactModalComponent {
  @Input() extractedContacts: any[] = [];
  @Input() selectedFileCount: number = 0;

  isSaving = false;

  readonly GRID_COLUMNS: EditableGridColumn[] = [
    { key: 'contactType', label: 'Type', type: 'select', options: ['Guest', 'Agent', 'Dmc', 'Vendor'] },
    { key: 'organizationName', label: 'Organization / Company', type: 'text' },
    { key: 'firstName', label: 'First name', type: 'text' },
    { key: 'lastName', label: 'Last name', type: 'text' },
    { key: 'phoneCountryCode', label: 'Code', type: 'text' },
    { key: 'phoneNumber', label: 'Phone', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'websiteLink', label: 'Website', type: 'text' },
    { key: 'address1', label: 'Address', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'country', label: 'Country', type: 'text' },
    { key: 'dateOfBirth', label: 'DOB', type: 'text' },
  ];

  readonly REQUIRED_FIELDS_BY_TYPE: { [key: string]: string[] } = {
    Guest: ['firstName', 'phoneNumber'],
    Dmc: ['organizationName', 'firstName', 'phoneNumber', 'email', 'address1'],
    Vendor: ['organizationName', 'firstName', 'phoneNumber', 'email', 'address1'],
    Agent: ['organizationName', 'firstName', 'phoneNumber', 'email', 'address1'],
  };

  constructor(
    private _modalController: ModalController,
    private _contactService: ContactsService,
    private _globalService: GlobalService,
    private _reloadService: ComponentReloadService,
    private _responseNotificationServce: ResponseNotificationService,
  ) { }

  onGridRowsChange(rows: any[]) {
    this.extractedContacts = rows;
  }

  selectedCount(): number {
    return this.extractedContacts.filter(e => e._include).length;
  }

  private validateEmail(email: any): boolean {
    if (!email || !email.toString().trim()) {
      return false;
    }

    const emailPattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email.toString().trim());
  }

  private validateContact(contact: any): string | null {
    if (!contact.contactType) {
      return 'Type is required';
    }

    const requiredFields = this.REQUIRED_FIELDS_BY_TYPE[contact.contactType] || [];

    const missing = requiredFields.filter(
      field => !contact[field] || !contact[field].toString().trim()
    );

    if (missing.length > 0) {
      const labels = missing.map(
        field => this.GRID_COLUMNS.find(c => c.key === field)?.label || field
      );

      return `Missing ${labels.join(', ')}`;
    }

    if (contact.email && !this.validateEmail(contact.email)) {
      return 'Invalid email format';
    }

    return null;
  }

  canSave(): boolean {
    const selected = this.extractedContacts.filter(e => e._include);

    if (selected.length === 0) {
      return false;
    }

    return selected.every(contact => this.validateContact(contact) === null);
  }

  private sanitizeForSave(contact: any): any {
    const sanitized: any = {};

    for (const key of Object.keys(contact)) {
      const value = contact[key];

      if (typeof value === 'string' && value.trim() === '') {
        sanitized[key] = null;
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  saveAll() {
    const selected = this.extractedContacts.filter(e => e._include);

    if (!this.canSave()) {
      return;
    }

    const toSave = selected.map(({ _include, ...rest }) =>
      this.sanitizeForSave(rest)
    );

    this.isSaving = true;

    this._contactService.saveContacts(toSave).subscribe({
      next: (result: any) => {
        this.isSaving = false;

        const results = result.results || [];
        const successCount = results.filter((r: any) => r.success).length;
        const failCount = results.length - successCount;

        this.triggerReloadsForSavedTypes(toSave, results);

        if (failCount === 0) {
          this._responseNotificationServce.showMessage(
            `${successCount} contact(s) saved successfully`
          );
        } else {
          this._responseNotificationServce.showMessage(
            `${successCount} contact(s) saved, ${failCount} failed. Please try again for the failed ones.`,
            true
          );
        }

        this._modalController.dismiss({ saved: true });
      },
      error: () => {
        this.isSaving = false;
        this._globalService.customAlert(
          'Could not save contacts. Try again.',
          'Save failed'
        );
      }
    });
  }

  private triggerReloadsForSavedTypes(sentEntries: any[], results: any[]) {
    const savedTypes = new Set<string>();

    results.forEach((result: any, index: number) => {
      if (result.success) {
        const type = sentEntries[index]?.contactType;

        if (type) {
          savedTypes.add(type);
        }
      }
    });

    if (savedTypes.has('Guest')) {
      this._reloadService.triggerContactsReload();
    }

    if (savedTypes.has('Dmc')) {
      this._reloadService.triggerDMCContactsReload();
    }

    if (savedTypes.has('Vendor')) {
      this._reloadService.triggerVendorContactsReload();
    }

    if (savedTypes.has('Agent')) {
      this._reloadService.triggerAgentContactsReload();
    }
  }

  closeModal() {
    this._modalController.dismiss();
  }
}